<template>
    <div>
        <app-template>
            <div class="tf-app border">
                
                <!-- ══ HEADER ══ -->
                <header class="tf-header">
                    <nav class="tf-periode-nav">
                        <button v-for="p in periodes" :key="p.v"
                        class="tf-pnav__btn" :class="{ active: periode === p.v }"
                        @click="setPeriode(p.v)">{{ p.l }}</button>
                    </nav>
                    
                    <div class="tf-header__right">
                        <button class="tf-refresh" :class="{ spinning: loading }" @click="load" title="Actualiser">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                            </svg>
                        </button>
                    </div>
                </header>
                
                <!-- ══ ALERTES ══ -->
                <div class="tf-alertes" v-if="alertes.length">
                    <div v-for="a in alertes" :key="a.code" class="tf-alerte" :class="`tf--${a.type}`">
                        <span class="tf-alerte__dot"></span>{{ a.message }}
                    </div>
                </div>
                
                <!-- ══ SKELETON ══ -->
                <div class="tf-skeleton-wrap" v-if="loading">
                    <div class="tf-skeleton-kpis">
                        <div class="tf-sk" v-for="i in 6" :key="i"></div>
                    </div>
                    <div class="tf-sk tf-sk--wide" style="height:320px;margin-top:20px"></div>
                </div>
                
                <div v-else class="tf-body">
                    
                    <!-- ══ TABS ══ -->
                    <div class="tf-tabs">
                        <button v-for="t in tabs" :key="t.id"
                        class="tf-tab" :class="{ 'tf-tab--active': activeTab === t.id }"
                        @click="activeTab = t.id">
                        <span class="tf-tab__icon" v-html="t.icon"></span>
                        <span>{{ t.label }}</span>
                        <span class="tf-tab__badge" v-if="t.badge">{{ t.badge }}</span>
                    </button>
                </div>
                
                <!-- ══════════════════════════════════════
                TAB 1 — VUE GLOBALE
                ══════════════════════════════════════ -->
                <div v-show="activeTab === 'global'" class="tf-tab-content">
                    <div class="tf-kpi-strip">
                        <div class="tf-kpi tf-kpi--hero">
                            <div class="tf-kpi__lbl">Chiffre d'affaires HT</div>
                            <div class="tf-kpi__val">{{ fmtEuro(kpis.ca_ht && kpis.ca_ht.valeur) }}</div>
                            <div class="tf-kpi__foot">
                                <span class="tf-badge" :class="deltaClass(kpis.ca_ht && kpis.ca_ht.delta)">
                                    {{ deltaIcon(kpis.ca_ht && kpis.ca_ht.delta) }} {{ Math.abs(kpis.ca_ht && kpis.ca_ht.delta || 0) }}%
                                </span>
                                <span class="tf-kpi__prev">vs {{ fmtEuro(kpis.ca_ht && kpis.ca_ht.prev) }}</span>
                            </div>
                        </div>
                        <div class="tf-kpi">
                            <div class="tf-kpi__lbl">Interventions</div>
                            <div class="tf-kpi__val">{{ fmtN(kpis.nb_interventions && kpis.nb_interventions.valeur) }}</div>
                            <div class="tf-kpi__foot">
                                <span class="tf-badge" :class="deltaClass(kpis.nb_interventions && kpis.nb_interventions.delta)">
                                    {{ deltaIcon(kpis.nb_interventions && kpis.nb_interventions.delta) }} {{ Math.abs(kpis.nb_interventions && kpis.nb_interventions.delta || 0) }}%
                                </span>
                            </div>
                        </div>
                        <div class="tf-kpi">
                            <div class="tf-kpi__lbl">Clients</div>
                            <div class="tf-kpi__val">{{ fmtN(kpis.nb_clients && kpis.nb_clients.valeur) }}</div>
                            <div class="tf-kpi__foot">
                                <span class="tf-badge" :class="deltaClass(kpis.nb_clients && kpis.nb_clients.delta)">
                                    {{ deltaIcon(kpis.nb_clients && kpis.nb_clients.delta) }} {{ Math.abs(kpis.nb_clients && kpis.nb_clients.delta || 0) }}%
                                </span>
                            </div>
                        </div>
                        <div class="tf-kpi">
                            <div class="tf-kpi__lbl">MRR Contrats</div>
                            <div class="tf-kpi__val">{{ fmtEuro(kpis.mrr) }}</div>
                            <div class="tf-kpi__sub">Revenus récurrents / mois</div>
                        </div>
                        <div class="tf-kpi">
                            <div class="tf-kpi__lbl">Taux complétion</div>
                            <div class="tf-kpi__val">{{ kpis.taux_completion || 0 }}%</div>
                            <div class="tf-progress"><div class="tf-progress__fill" :style="{ width: (kpis.taux_completion||0)+'%' }"></div></div>
                        </div>
                        <div class="tf-kpi">
                            <div class="tf-kpi__lbl">Techniciens actifs</div>
                            <div class="tf-kpi__val">{{ kpis.tech_actifs || 0 }}</div>
                            <div class="tf-kpi__sub">Sur la période</div>
                        </div>
                    </div>
                    
                    <!-- Courbe CA 12 mois -->
                    <div class="tf-grid tf-grid--full">
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Évolution CA — 12 mois glissants</h3><span class="tf-chip">HT</span></div>
                            <div class="tf-chart-wrap" style="height:220px">
                                <svg ref="casvg" width="100%" height="220" v-if="caEvolution.length" style="overflow:visible">
                                    <defs>
                                        <linearGradient id="gca" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stop-color="#1B2E4B" stop-opacity=".13"/>
                                            <stop offset="100%" stop-color="#1B2E4B" stop-opacity="0"/>
                                        </linearGradient>
                                    </defs>
                                    <g :transform="`translate(56,12)`">
                                        <line v-for="g in caGrid" :key="'g'+g.y" x1="0" :y1="g.y" :x2="caW" :y2="g.y" stroke="#E8EDF3" stroke-width="1"/>
                                        <text v-for="g in caGrid" :key="'gl'+g.y" x="-8" :y="g.y+4" text-anchor="end" class="tf-axis-lbl">{{ fmtK(g.v) }}</text>
                                        <path :d="caArea" fill="url(#gca)"/>
                                        <path :d="caLine" fill="none" stroke="#1B2E4B" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        <g v-for="(p,i) in caPts" :key="i">
                                            <circle :cx="p.x" :cy="p.y" r="4" fill="white" stroke="#1B2E4B" stroke-width="2"/>
                                            <text :x="p.x" :y="caH+20" text-anchor="middle" class="tf-axis-lbl">{{ p.label }}</text>
                                        </g>
                                    </g>
                                </svg>
                                <div class="tf-empty" v-else>Aucune donnée disponible</div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- ══════════════════════════════════════
                TAB 2 — TECHNICIENS
                ══════════════════════════════════════ -->
                <div v-show="activeTab === 'techniciens'" class="tf-tab-content">
                    <div class="tf-section-title">Performance individuelle</div>
                    
                    <!-- Classement techniciens -->
                    <div class="tf-card tf-card--flush">
                        <div class="tf-card__hd"><h3>Classement techniciens</h3><span class="tf-chip">Période sélectionnée</span></div>
                        <div class="tf-table-wrap">
                            <table class="tf-table">
                                <thead><tr>
                                    <th style="width:36px">#</th>
                                    <th>Technicien</th>
                                    <th class="r">Interventions</th>
                                    <th class="r">Complétées</th>
                                    <th class="r">Taux ✓</th>
                                    <th class="r">CA généré</th>
                                    <th class="r">H. terrain</th>
                                    <th class="r">KM total</th>
                                    <th class="r">Durée moy.</th>
                                    <th class="r">Perf.</th>
                                </tr></thead>
                                <tbody>
                                    <tr v-for="(t,i) in techClassement" :key="t.id" class="tf-tr">
                                        <td><span class="tf-rank" :class="['r1','r2','r3'][i]||''">{{ i+1 }}</span></td>
                                        <td><div class="tf-person"><div class="tf-avatar">{{ initials(t.nom) }}</div><span>{{ t.nom }}</span></div></td>
                                        <td class="r tf-mono">{{ t.nb_interventions }}</td>
                                        <td class="r tf-mono">{{ t.nb_completes }}</td>
                                        <td class="r"><span class="tf-pct" :class="t.taux_completion >= 80 ? 'ok' : t.taux_completion >= 50 ? 'warn' : 'bad'">{{ t.taux_completion }}%</span></td>
                                        <td class="r tf-mono">{{ fmtEuro(t.ca_genere) }}</td>
                                        <td class="r tf-mono">{{ fmtH(t.heures_terrain * 60) }}</td>
                                        <td class="r tf-mono">{{ t.km_total }} km</td>
                                        <td class="r tf-mono">{{ t.duree_moy_min }} min</td>
                                        <td class="r"><div class="tf-ibar"><div class="tf-ibar__fill" :style="{ width: barPct(t.nb_interventions, maxTech)+'%' }"></div></div></td>
                                    </tr>
                                    <tr v-if="!techClassement.length"><td colspan="10" class="tf-empty">Aucune donnée</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    <div class="tf-grid tf-grid--2" style="margin-top:16px">
                        <!-- Occupation planning -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Taux d'occupation</h3><span class="tf-chip">Planning</span></div>
                            <div class="tf-occ-list">
                                <div v-for="t in techOccupation" :key="t.id" class="tf-occ-item">
                                    <div class="tf-occ-item__head">
                                        <div class="tf-person"><div class="tf-avatar tf-avatar--sm">{{ initials(t.nom) }}</div><span>{{ t.nom }}</span></div>
                                        <span class="tf-pct" :class="t.taux_occupation >= 70 ? 'ok' : t.taux_occupation >= 40 ? 'warn' : 'bad'">{{ t.taux_occupation }}%</span>
                                    </div>
                                    <div class="tf-progress"><div class="tf-progress__fill" :class="t.taux_occupation >= 70 ? 'tf-progress__fill--ok' : ''" :style="{ width: t.taux_occupation+'%' }"></div></div>
                                    <div class="tf-occ-item__sub">{{ fmtH(t.minutes_intervention) }} intervention · {{ fmtH(t.minutes_total) }} planifié</div>
                                </div>
                                <div class="tf-empty" v-if="!techOccupation.length">Aucune donnée planning</div>
                            </div>
                        </div>
                        
                        <!-- Absences -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Absences</h3><span class="tf-chip">Période</span></div>
                            <div class="tf-abs-types">
                                <div v-for="a in absencesParType" :key="a.type" class="tf-abs-type">
                                    <span class="tf-abs-type__dot" :style="{ background: absColor(a.type) }"></span>
                                    <span class="tf-abs-type__label">{{ absLabel(a.type) }}</span>
                                    <span class="tf-abs-type__nb">{{ a.nb }}</span>
                                    <span class="tf-abs-type__j">{{ a.total_jours }}j</span>
                                </div>
                            </div>
                            <div class="tf-abs-list">
                                <div v-for="a in absences.slice(0,8)" :key="a.id||a.date_debut" class="tf-abs-item">
                                    <div class="tf-abs-item__who"><div class="tf-avatar tf-avatar--sm">{{ initials(a.nom) }}</div><span>{{ a.nom }}</span></div>
                                    <span class="tf-abs-badge" :style="{ background: absColor(a.type) }">{{ absLabel(a.type) }}</span>
                                    <span class="tf-abs-item__dates">{{ a.date_debut }} → {{ a.date_fin }}</span>
                                    <span class="tf-abs-item__j">{{ a.nb_jours }}j</span>
                                </div>
                                <div class="tf-empty" v-if="!absences.length">Aucune absence sur la période</div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- ══════════════════════════════════════
                TAB 3 — FINANCE
                ══════════════════════════════════════ -->
                <div v-show="activeTab === 'finance'" class="tf-tab-content">
                    
                    <!-- KPIs finance -->
                    <div class="tf-fin-kpis">
                        <div class="tf-fin-kpi tf-fin-kpi--primary"><span class="tf-fin-kpi__lbl">CA HT période</span><span class="tf-fin-kpi__val">{{ fmtEuro(finCaTotal) }}</span></div>
                        <div class="tf-fin-kpi"><span class="tf-fin-kpi__lbl">Panier moyen</span><span class="tf-fin-kpi__val">{{ fmtEuro(finance.panier_moyen) }}</span></div>
                        <div class="tf-fin-kpi"><span class="tf-fin-kpi__lbl">DSO moyen</span><span class="tf-fin-kpi__val">{{ finance.dso_moyen || 0 }}<small>j</small></span></div>
                        <div class="tf-fin-kpi tf-fin-kpi--warn"><span class="tf-fin-kpi__lbl">Souffrance > 30j</span><span class="tf-fin-kpi__val">{{ fmtEuro(finance.en_souffrance_30) }}</span></div>
                        <div class="tf-fin-kpi tf-fin-kpi--danger"><span class="tf-fin-kpi__lbl">Souffrance > 60j</span><span class="tf-fin-kpi__val">{{ fmtEuro(finance.en_souffrance_60) }}</span></div>
                        <div class="tf-fin-kpi tf-fin-kpi--gold"><span class="tf-fin-kpi__lbl">MRR</span><span class="tf-fin-kpi__val">{{ fmtEuro(finance.mrr) }}</span></div>
                        <div class="tf-fin-kpi"><span class="tf-fin-kpi__lbl">ARR</span><span class="tf-fin-kpi__val">{{ fmtEuro(finance.arr) }}</span></div>
                        <div class="tf-fin-kpi"><span class="tf-fin-kpi__lbl">Relances</span><span class="tf-fin-kpi__val">{{ finance.relances || 0 }}</span></div>
                    </div>
                    
                    <div class="tf-grid tf-grid--2" style="margin-top:16px">
                        <!-- CA par type -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>CA par type d'intervention</h3></div>
                            <div class="tf-bars-list">
                                <div v-for="(t,i) in finance.ca_par_type" :key="i" class="tf-bar-row">
                                    <div class="tf-bar-row__meta"><span>{{ t.type }}</span><span class="tf-mono">{{ fmtEuro(t.ca_ht) }}</span></div>
                                    <div class="tf-track"><div class="tf-track__fill" :style="{ width: barPct(t.ca_ht, maxCaType)+'%', background: palette[i % palette.length] }"></div></div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Top clients -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Top 10 clients</h3><span class="tf-chip">CA HT</span></div>
                            <div class="tf-ranked">
                                <div v-for="(c,i) in finance.top_clients" :key="c.id" class="tf-ranked__item">
                                    <span class="tf-ranked__pos">{{ i+1 }}</span>
                                    <div class="tf-ranked__info"><span class="tf-ranked__name">{{ c.nom }}</span><span class="tf-ranked__sub">{{ c.nb_factures }} facture{{ c.nb_factures > 1 ? 's' : '' }} · moy. {{ fmtEuro(c.panier_moyen) }}</span></div>
                                    <span class="tf-ranked__val">{{ fmtEuro(c.ca_ht) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Contrats expirant -->
                    <div class="tf-card" style="margin-top:16px" v-if="finance.contrats_expirant && finance.contrats_expirant.length">
                        <div class="tf-card__hd"><h3>Contrats expirant bientôt</h3><span class="tf-chip tf-chip--warn">{{ finance.contrats_expirant.length }} à renouveler</span></div>
                        <div class="tf-table-wrap">
                            <table class="tf-table">
                                <thead><tr><th>N° Contrat</th><th>Client</th><th class="r">Valeur</th><th class="r">Expire le</th><th class="r">Jours restants</th></tr></thead>
                                <tbody>
                                    <tr v-for="c in finance.contrats_expirant" :key="c.id" class="tf-tr">
                                        <td class="tf-mono">{{ c.numero_contrat }}</td>
                                        <td>{{ c.client }}</td>
                                        <td class="r tf-mono">{{ fmtEuro(c.prix) }}</td>
                                        <td class="r">{{ c.date_fin }}</td>
                                        <td class="r"><span class="tf-pct" :class="c.jours_restants <= 15 ? 'bad' : c.jours_restants <= 30 ? 'warn' : 'ok'">{{ c.jours_restants }}j</span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <!-- ══════════════════════════════════════
                TAB 4 — INTERVENTIONS
                ══════════════════════════════════════ -->
                <div v-show="activeTab === 'interventions'" class="tf-tab-content">
                    
                    <!-- KPIs interventions -->
                    <div class="tf-kpi-strip">
                        <div class="tf-kpi"><div class="tf-kpi__lbl">Total</div><div class="tf-kpi__val">{{ fmtN(interv.total) }}</div></div>
                        <div class="tf-kpi tf-kpi--hero"><div class="tf-kpi__lbl">Taux complétion</div><div class="tf-kpi__val">{{ interv.taux_completion }}%</div><div class="tf-progress"><div class="tf-progress__fill" :style="{ width: interv.taux_completion+'%' }"></div></div></div>
                        <div class="tf-kpi"><div class="tf-kpi__lbl">Durée moy. réelle</div><div class="tf-kpi__val">{{ interv.durees && interv.durees.reelle_moy || 0 }}<small>min</small></div></div>
                        <div class="tf-kpi"><div class="tf-kpi__lbl">Durée moy. estimée</div><div class="tf-kpi__val">{{ interv.durees && interv.durees.estimee_moy || 0 }}<small>min</small></div></div>
                        <div class="tf-kpi"><div class="tf-kpi__lbl">KM total</div><div class="tf-kpi__val">{{ interv.trajets && interv.trajets.km_total || 0 }}<small>km</small></div></div>
                        <div class="tf-kpi"><div class="tf-kpi__lbl">Coût trajets</div><div class="tf-kpi__val">{{ fmtEuro(interv.trajets && interv.trajets.cout_total) }}</div></div>
                    </div>
                    
                    <div class="tf-grid tf-grid--3" style="margin-top:16px">
                        <!-- Par type -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Par type</h3></div>
                            <div class="tf-bars-list">
                                <div v-for="(t,i) in interv.par_type" :key="i" class="tf-bar-row">
                                    <div class="tf-bar-row__meta"><span>{{ t.type }}</span><span class="tf-mono">{{ t.nb }}</span></div>
                                    <div class="tf-track"><div class="tf-track__fill" :style="{ width: barPct(t.nb, maxIntervType)+'%', background: palette[i % palette.length] }"></div></div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Par statut -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Par statut</h3></div>
                            <div class="tf-bars-list">
                                <div v-for="(s,i) in interv.par_statut" :key="i" class="tf-bar-row">
                                    <div class="tf-bar-row__meta">
                                        <span class="tf-dot" :style="{ background: s.couleur }"></span>
                                        <span>{{ s.statut }}</span><span class="tf-mono">{{ s.nb }}</span>
                                    </div>
                                    <div class="tf-track"><div class="tf-track__fill" :style="{ width: barPct(s.nb, maxIntervStatut)+'%', background: s.couleur }"></div></div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Heatmap semaine -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Activité par jour</h3></div>
                            <div class="tf-heatmap">
                                <div v-for="d in weekDays" :key="d.label" class="tf-hm-col">
                                    <div class="tf-hm-bar-wrap">
                                        <div class="tf-hm-bar" :style="{ height: heatH(d.nb)+'px', opacity: heatOp(d.nb) }"></div>
                                    </div>
                                    <span class="tf-hm-lbl">{{ d.label }}</span>
                                    <span class="tf-hm-nb">{{ d.nb }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="tf-grid tf-grid--2" style="margin-top:16px">
                        <!-- Heure de démarrage -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Heure de démarrage</h3><span class="tf-chip">Distribution horaire</span></div>
                            <div class="tf-hourly">
                                <div v-for="h in hourlyBars" :key="h.heure" class="tf-hourly__col">
                                    <div class="tf-hourly__bar-wrap">
                                        <div class="tf-hourly__bar" :style="{ height: barPct(h.nb, maxHourly)*0.8+'%' }"></div>
                                    </div>
                                    <span class="tf-hourly__lbl">{{ h.heure }}h</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Documents -->
                        <div class="tf-card">
                            <div class="tf-card__hd"><h3>Documents & conformité</h3></div>
                            <div class="tf-doc-stats">
                                <div class="tf-doc-stat">
                                    <div class="tf-doc-stat__ring">
                                        <svg width="72" height="72" viewBox="0 0 72 72">
                                            <circle cx="36" cy="36" r="28" fill="none" stroke="#F0F3F8" stroke-width="8"/>
                                            <circle cx="36" cy="36" r="28" fill="none" :stroke="interv.bons && interv.bons.taux >= 80 ? '#22C55E' : '#C9A84C'" stroke-width="8" stroke-linecap="round" :stroke-dasharray="`${176 * (interv.bons && interv.bons.taux || 0) / 100} 176`" transform="rotate(-90 36 36)"/>
                                                <text x="36" y="40" text-anchor="middle" class="tf-ring-val">{{ interv.bons && interv.bons.taux || 0 }}%</text>
                                            </svg>
                                        </div>
                                        <div class="tf-doc-stat__info"><div class="tf-doc-stat__label">Bons signés</div><div class="tf-doc-stat__sub">{{ interv.bons && interv.bons.signes }}/{{ interv.bons && interv.bons.total }}</div></div>
                                    </div>
                                    <div class="tf-doc-stat">
                                        <div class="tf-doc-stat__ring">
                                            <svg width="72" height="72" viewBox="0 0 72 72">
                                                <circle cx="36" cy="36" r="28" fill="none" stroke="#F0F3F8" stroke-width="8"/>
                                                <circle cx="36" cy="36" r="28" fill="none" :stroke="(interv.cerfas && interv.cerfas.taux_fuite || 0) > 10 ? '#EF4444' : '#22C55E'" stroke-width="8" stroke-linecap="round" :stroke-dasharray="`${176 * (100 - (interv.cerfas && interv.cerfas.taux_fuite || 0)) / 100} 176`" transform="rotate(-90 36 36)"/>
                                                    <text x="36" y="40" text-anchor="middle" class="tf-ring-val">{{ 100 - (interv.cerfas && interv.cerfas.taux_fuite || 0) }}%</text>
                                                </svg>
                                            </div>
                                            <div class="tf-doc-stat__info"><div class="tf-doc-stat__label">CERFAs sans fuite</div><div class="tf-doc-stat__sub">{{ interv.cerfas && interv.cerfas.fuites }} fuite{{ interv.cerfas && interv.cerfas.fuites > 1 ? 's' : '' }}/{{ interv.cerfas && interv.cerfas.total }}</div></div>
                                        </div>
                                        <div class="tf-doc-stat">
                                            <div class="tf-doc-stat__num">{{ interv.pv_receptions || 0 }}</div>
                                            <div class="tf-doc-stat__info"><div class="tf-doc-stat__label">PV de réception</div></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- ══════════════════════════════════════
                        TAB 5 — CLIENTS
                        ══════════════════════════════════════ -->
                        <div v-show="activeTab === 'clients'" class="tf-tab-content">
                            
                            <!-- KPIs clients -->
                            <div class="tf-kpi-strip">
                                <div class="tf-kpi tf-kpi--hero"><div class="tf-kpi__lbl">Total clients</div><div class="tf-kpi__val">{{ fmtN(clients.total) }}</div></div>
                                <div class="tf-kpi"><div class="tf-kpi__lbl">Taux fidélisation</div><div class="tf-kpi__val">{{ clients.taux_fidelisation || 0 }}%</div><div class="tf-progress"><div class="tf-progress__fill tf-progress__fill--ok" :style="{ width: (clients.taux_fidelisation||0)+'%' }"></div></div></div>
                                <div class="tf-kpi"><div class="tf-kpi__lbl">Contrats actifs</div><div class="tf-kpi__val">{{ fmtN(clients.contrats_actifs) }}</div></div>
                                <div class="tf-kpi tf-kpi--warn"><div class="tf-kpi__lbl">Inactifs > 6 mois</div><div class="tf-kpi__val">{{ fmtN(clients.inactifs_6m) }}</div></div>
                                <div class="tf-kpi tf-kpi--danger"><div class="tf-kpi__lbl">Inactifs > 12 mois</div><div class="tf-kpi__val">{{ fmtN(clients.inactifs_12m) }}</div></div>
                                <div class="tf-kpi"><div class="tf-kpi__lbl">Appareils anciens</div><div class="tf-kpi__val">{{ fmtN(clients.appareils_anciens) }}</div><div class="tf-kpi__sub">installation > 10 ans</div></div>
                            </div>
                            
                            <div class="tf-grid tf-grid--2" style="margin-top:16px">
                                <!-- Top clients actifs -->
                                <div class="tf-card">
                                    <div class="tf-card__hd"><h3>Clients les plus actifs</h3><span class="tf-chip">Interventions</span></div>
                                    <div class="tf-ranked">
                                        <div v-for="(c,i) in (clients.plus_actifs||[]).slice(0,10)" :key="c.id" class="tf-ranked__item">
                                            <span class="tf-ranked__pos">{{ i+1 }}</span>
                                            <div class="tf-ranked__info">
                                                <span class="tf-ranked__name">{{ c.nom }}</span>
                                                <span class="tf-ranked__sub">{{ c.ville }}</span>
                                            </div>
                                            <span class="tf-ranked__val">{{ c.nb_interventions }} interv.</span>
                                        </div>
                                        <div class="tf-empty" v-if="!(clients.plus_actifs||[]).length">Aucun client</div>
                                    </div>
                                </div>
                                
                                <!-- Appareils par type -->
                                <div class="tf-card">
                                    <div class="tf-card__hd"><h3>Parc d'appareils</h3><span class="tf-chip">Par type</span></div>
                                    <div class="tf-bars-list">
                                        <div v-for="(a,i) in (clients.appareils_par_type||[]).slice(0,10)" :key="i" class="tf-bar-row">
                                            <div class="tf-bar-row__meta">
                                                <span>{{ a.type }}<small v-if="a.marque"> · {{ a.marque }}</small></span>
                                                <span class="tf-mono">{{ a.nb }}</span>
                                            </div>
                                            <div class="tf-track"><div class="tf-track__fill" :style="{ width: barPct(a.nb, maxAppareil)+'%', background: palette[i % palette.length] }"></div></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="tf-grid tf-grid--2" style="margin-top:16px">
                                <!-- Répartition par ville -->
                                <div class="tf-card">
                                    <div class="tf-card__hd"><h3>Répartition géographique</h3><span class="tf-chip">Top villes</span></div>
                                    <div class="tf-bars-list">
                                        <div v-for="(v,i) in (clients.par_ville||[]).slice(0,12)" :key="i" class="tf-bar-row">
                                            <div class="tf-bar-row__meta"><span>{{ v.ville }}</span><span class="tf-mono">{{ v.nb }}</span></div>
                                            <div class="tf-track"><div class="tf-track__fill" :style="{ width: barPct(v.nb, maxVille)+'%' }"></div></div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Nouveaux clients avec CA -->
                                <div class="tf-card">
                                    <div class="tf-card__hd"><h3>Nouveaux clients</h3><span class="tf-chip">CA généré</span></div>
                                    <div class="tf-ranked">
                                        <div v-for="(c,i) in (clients.nouveaux_avec_ca||[]).slice(0,8)" :key="c.id" class="tf-ranked__item">
                                            <span class="tf-ranked__pos">{{ i+1 }}</span>
                                            <div class="tf-ranked__info"><span class="tf-ranked__name">{{ c.nom }}</span><span class="tf-ranked__sub">{{ c.date_creation && c.date_creation.substring(0,10) }}</span></div>
                                            <span class="tf-ranked__val">{{ fmtEuro(c.ca_ht) }}</span>
                                        </div>
                                        <div class="tf-empty" v-if="!(clients.nouveaux_avec_ca||[]).length">Aucun nouveau client facturé</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- ══════════════════════════════════════
                        TAB 6 — STOCK
                        ══════════════════════════════════════ -->
                        <div v-show="activeTab === 'stock'" class="tf-tab-content">
                            
                            <!-- Ruptures d'abord -->
                            <div class="tf-ruptures" v-if="stock.ruptures && stock.ruptures.length">
                                <div class="tf-ruptures__icon">⚠</div>
                                <div class="tf-ruptures__list">
                                    <span v-for="r in stock.ruptures" :key="r.produit+r.emplacement" class="tf-ruptures__item">{{ r.produit }} ({{ r.emplacement }})</span>
                                </div>
                            </div>
                            
                            <div class="tf-grid tf-grid--2" style="margin-top:16px">
                                <!-- Top consommés -->
                                <div class="tf-card">
                                    <div class="tf-card__hd"><h3>Top produits consommés</h3><span class="tf-chip">Période</span></div>
                                    <div class="tf-bars-list">
                                        <div v-for="(p,i) in (stock.plus_consommes||[]).slice(0,12)" :key="i" class="tf-bar-row">
                                            <div class="tf-bar-row__meta">
                                                <span>{{ p.produit }}<small v-if="p.reference"> · {{ p.reference }}</small></span>
                                                <span class="tf-mono">{{ p.qtt_consommee }} {{ p.unit }}</span>
                                            </div>
                                            <div class="tf-track"><div class="tf-track__fill" :style="{ width: barPct(p.qtt_consommee, maxConsomme)+'%', background: palette[i % palette.length] }"></div></div>
                                        </div>
                                        <div class="tf-empty" v-if="!(stock.plus_consommes||[]).length">Aucun mouvement</div>
                                    </div>
                                </div>
                                
                                <!-- Par emplacement -->
                                <div class="tf-card">
                                    <div class="tf-card__hd"><h3>Mouvements par emplacement</h3><span class="tf-chip">Période</span></div>
                                    <div class="tf-table-wrap">
                                        <table class="tf-table">
                                            <thead><tr><th>Emplacement</th><th>Type</th><th class="r">Entrées</th><th class="r">Sorties</th></tr></thead>
                                            <tbody>
                                                <tr v-for="(e,i) in (stock.par_emplacement||[])" :key="i" class="tf-tr">
                                                    <td>{{ e.emplacement }}</td>
                                                    <td><span class="tf-chip tf-chip--sm">{{ e.type }}</span></td>
                                                    <td class="r tf-mono" style="color:#22C55E">+{{ e.entrees }}</td>
                                                    <td class="r tf-mono" style="color:#EF4444">-{{ e.sorties }}</td>
                                                </tr>
                                                <tr v-if="!(stock.par_emplacement||[]).length"><td colspan="4" class="tf-empty">Aucun mouvement</td></tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Stock actuel -->
                            <div class="tf-card" style="margin-top:16px">
                                <div class="tf-card__hd"><h3>Stock actuel</h3><span class="tf-chip">Quantités positives uniquement</span></div>
                                <div class="tf-table-wrap">
                                    <table class="tf-table">
                                        <thead><tr><th>Produit</th><th>Référence</th><th>Emplacement</th><th>Type</th><th class="r">Quantité</th><th>Unité</th></tr></thead>
                                        <tbody>
                                            <tr v-for="(s,i) in (stock.stock_actuel||[])" :key="i" class="tf-tr">
                                                <td>{{ s.produit }}</td>
                                                <td class="tf-mono">{{ s.reference || '—' }}</td>
                                                <td>{{ s.emplacement }}</td>
                                                <td><span class="tf-chip tf-chip--sm">{{ s.type_emplacement }}</span></td>
                                                <td class="r tf-mono">{{ s.qtt_actuelle }}</td>
                                                <td>{{ s.unit }}</td>
                                            </tr>
                                            <tr v-if="!(stock.stock_actuel||[]).length"><td colspan="6" class="tf-empty">Aucun stock</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        
                    </div><!-- /tf-body -->
                </div>
            </app-template>
        </div>
    </template>
    
    <script>
    export default {
        name: 'DashboardPremium',
        
        data() {
            return {
                loading: true,
                periode: 'month',
                periodes: [
                { v: 'week', l: 'Semaine' }, { v: 'month', l: 'Mois' },
                { v: 'quarter', l: 'Trimestre' }, { v: 'year', l: 'Année' },
                ],
                activeTab: 'global',
                tabs: [
                { id: 'global',        label: 'Vue globale',   icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>' },
                { id: 'techniciens',   label: 'Techniciens',   icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>' },
                { id: 'finance',       label: 'Finance',       icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>' },
                { id: 'interventions', label: 'Interventions', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>' },
                { id: 'clients',       label: 'Clients',       icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>' },
                { id: 'stock',         label: 'Stock',         icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>' },
                ],
                
                // Data par onglet
                kpis:     {},
                caEvolution: [],
                alertes:  [],
                techClassement:  [],
                techOccupation:  [],
                absences:        [],
                absencesParType: [],
                finance:  {},
                interv:   {},
                clients:  {},
                stock:    {},
                
                // Chart
                caW: 600, caH: 168,
                palette: ['#1B2E4B','#C9A84C','#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444','#06B6D4'],
            }
        },
        
        computed: {
            todayLabel() {
                return new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })
            },
            
            // CA chart
            caValues() { return this.caEvolution.map(m => parseFloat(m.ca_ht) || 0) },
            caMax()    { return Math.max(...this.caValues, 1) },
            caGrid() {
                const steps = 4
                return Array.from({ length: steps + 1 }, (_, i) => ({
                    v:  (this.caMax / steps) * (steps - i),
                    y:  (this.caH / steps) * i,
                }))
            },
            caPts() {
                const n = this.caEvolution.length
                if (!n) return []
                return this.caEvolution.map((m, i) => ({
                    x: n > 1 ? (i / (n - 1)) * this.caW : this.caW / 2,
                    y: this.caH - (parseFloat(m.ca_ht) / this.caMax) * this.caH,
                    label: (m.mois || '').substring(5),
                    val: parseFloat(m.ca_ht) || 0,
                }))
            },
            caLine() {
                return this.caPts.reduce((d, p, i) => d + (i === 0 ? `M${p.x},${p.y}` : ` L${p.x},${p.y}`), '')
            },
            caArea() {
                if (!this.caPts.length) return ''
                const last = this.caPts[this.caPts.length - 1]
                return `${this.caLine} L${last.x},${this.caH} L${this.caPts[0].x},${this.caH} Z`
            },
            
            // Techniciens
            maxTech() { return Math.max(...this.techClassement.map(t => t.nb_interventions || 0), 1) },
            
            // Finance
            finCaTotal() {
                return (this.finance.ca_mensuel || []).reduce((s, m) => s + parseFloat(m.ca_ht || 0), 0)
            },
            maxCaType() { return Math.max(...(this.finance.ca_par_type || []).map(t => parseFloat(t.ca_ht) || 0), 1) },
            
            // Interventions
            maxIntervType()   { return Math.max(...(this.interv.par_type || []).map(t => t.nb || 0), 1) },
            maxIntervStatut() { return Math.max(...(this.interv.par_statut || []).map(s => s.nb || 0), 1) },
            weekDays() {
                const labels = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam']
                const map = this.interv.par_jour || {}
                return [2,3,4,5,6,7,1].map(d => ({
                    label: labels[d - 1],
                    nb: map[d] ? parseInt(map[d].nb) : 0,
                }))
            },
            maxWeek() { return Math.max(...this.weekDays.map(d => d.nb), 1) },
            hourlyBars() {
                const all = this.interv.par_heure || []
                return Array.from({ length: 12 }, (_, i) => {
                    const h = i + 7
                    const found = all.find(x => parseInt(x.heure) === h)
                    return { heure: h, nb: found ? parseInt(found.nb) : 0 }
                })
            },
            maxHourly() { return Math.max(...this.hourlyBars.map(h => h.nb), 1) },
            
            // Clients
            maxAppareil() { return Math.max(...(this.clients.appareils_par_type || []).map(a => a.nb || 0), 1) },
            maxVille()    { return Math.max(...(this.clients.par_ville || []).map(v => v.nb || 0), 1) },
            
            // Stock
            maxConsomme() { return Math.max(...(this.stock.plus_consommes || []).map(p => parseFloat(p.qtt_consommee) || 0), 1) },
        },
        
        mounted() {
            if (this.$account.account.societe.independant == 1) {
                window.location.href = '/app'
            }
            
            this.load()
            this.$nextTick(this.measureChart)
            window.addEventListener('resize', this.measureChart)
        },
        beforeDestroy() { window.removeEventListener('resize', this.measureChart) },
        
        methods: {
            async load() {
                this.loading = true
                try {
                    const data = await this.$request.get(`/dashboard/premium?periode=${this.periode}`)
                    this.kpis            = data.vue_globale?.kpis || {}
                    this.caEvolution     = data.vue_globale?.ca_evolution || []
                    this.alertes         = data.vue_globale?.alertes || []
                    this.techClassement  = data.techniciens?.classement || []
                    this.techOccupation  = data.techniciens?.occupation || []
                    this.absences        = data.techniciens?.absences || []
                    this.absencesParType = data.techniciens?.absences_par_type || []
                    this.finance         = data.finance || {}
                    this.interv          = data.interventions || {}
                    this.clients         = data.clients || {}
                    this.stock           = data.stock || {}
                } catch (e) {
                    console.error('Dashboard load error', e)
                } finally {
                    this.loading = false
                    this.$nextTick(this.measureChart)
                }
            },
            setPeriode(p) { this.periode = p; this.load() },
            measureChart() {
                if (this.$refs.casvg) {
                    const w = this.$refs.casvg.parentElement.clientWidth - 72
                    if (w > 0) this.caW = w
                }
            },
            
            // Formatters
            fmtEuro(v) {
                const n = parseFloat(v) || 0
                if (n >= 1e6) return (n / 1e6).toFixed(1) + ' M€'
                if (n >= 1e3) return (n / 1e3).toFixed(1) + ' k€'
                return n.toFixed(0) + ' €'
            },
            fmtK(v) {
                const n = parseFloat(v) || 0
                return n >= 1000 ? (n / 1000).toFixed(0) + 'k' : n.toFixed(0)
            },
            fmtN(v) { return (parseInt(v) || 0).toLocaleString('fr-FR') },
            fmtH(minutes) {
                const h = Math.floor((minutes || 0) / 60)
                const m = Math.round((minutes || 0) % 60)
                return `${h}h${m > 0 ? m + 'm' : ''}`
            },
            barPct(v, max) { return max > 0 ? Math.min(100, Math.round((v / max) * 100)) : 0 },
            heatH(nb)  { return Math.max(4, Math.round((nb / this.maxWeek) * 80)) },
            heatOp(nb) { return nb > 0 ? Math.max(0.25, nb / this.maxWeek) : 0.1 },
            
            deltaClass(d) {
                if (d == null) return 'tf-badge--neutral'
                return d >= 0 ? 'tf-badge--up' : 'tf-badge--down'
            },
            deltaIcon(d) { return (d == null || d >= 0) ? '↑' : '↓' },
            
            initials(nom) {
                if (!nom) return '?'
                return nom.trim().split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase()
            },
            absLabel(type) {
                return { conge:'Congé', maladie:'Maladie', rtt:'RTT', formation:'Formation', autre:'Autre' }[type] || type
            },
            absColor(type) {
                return { conge:'#3B82F6', maladie:'#EF4444', rtt:'#8B5CF6', formation:'#10B981', autre:'#94A3B8' }[type] || '#94A3B8'
            },
        },
    }
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

/* ═══ TOKENS ═══ */
.tf-app {
    --navy:      #1B2E4B;
    --navy2:     #243C62;
    --navy-lt:   #EBF0F7;
    --gold:      #C9A84C;
    --gold-lt:   #FDF6E3;
    --bg:        #F4F6FA;
    --surf:      #FFFFFF;
    --border:    #E2E8F2;
    --text:      #0F1C2E;
    --muted:     #7A8BA3;
    --green:     #22C55E;
    --warn:      #F59E0B;
    --danger:    #EF4444;
    --r:         12px;
    --r-sm:      8px;
    --sh:        0 2px 12px rgba(27,46,75,.07);
    --sh-lg:     0 8px 40px rgba(27,46,75,.10);
    font-family: 'DM Sans', sans-serif;
    color:       var(--text);
}

/* ═══ HEADER ═══ */
.tf-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; height: 58px; background: var(--surf);
    border-bottom: 1px solid var(--border);
    position: sticky; top: 0; z-index: 200;
    box-shadow: 0 1px 0 var(--border), 0 4px 16px rgba(27,46,75,.04);
}
.tf-header__brand { display:flex; align-items:center; gap:10px; }
.tf-brand__name   { font-size:15px; font-weight:700; color:var(--navy); letter-spacing:-.3px; }
.tf-brand__pill   { font-size:10px; font-weight:700; color:var(--gold); background:var(--gold-lt); border:1px solid rgba(201,168,76,.3); padding:2px 8px; border-radius:20px; text-transform:uppercase; letter-spacing:.5px; }
.tf-periode-nav   { display:flex; gap:2px; background:var(--bg); border:1px solid var(--border); border-radius:var(--r-sm); padding:3px; }
.tf-pnav__btn     { padding:5px 14px; border:none; background:transparent; font-size:12.5px; font-weight:500; color:var(--muted); border-radius:6px; cursor:pointer; transition:.15s; }
.tf-pnav__btn:hover { color:var(--navy); }
.tf-pnav__btn.active { background:var(--surf); color:var(--navy); font-weight:600; box-shadow:var(--sh); }
.tf-header__right { display:flex; align-items:center; gap:12px; }
.tf-header__date  { font-size:12px; color:var(--muted); }
.tf-refresh { width:32px; height:32px; border:1px solid var(--border); background:var(--surf); border-radius:var(--r-sm); display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--muted); transition:.15s; }
.tf-refresh:hover { color:var(--navy); border-color:var(--navy); }
.tf-refresh.spinning svg { animation: spin 1s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* ═══ ALERTES ═══ */
.tf-alertes { display:flex; flex-wrap:wrap; gap:8px; padding:10px 28px 0; }
.tf-alerte  { display:flex; align-items:center; gap:8px; font-size:12px; font-weight:500; padding:6px 12px; border-radius:6px; border:1px solid; animation:fadeIn .2s ease; }
.tf-alerte__dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
.tf--danger  { background:#FEF2F2; color:#991B1B; border-color:#FECACA; }
.tf--danger .tf-alerte__dot { background:#EF4444; }
.tf--warning { background:#FFFBEB; color:#92400E; border-color:#FDE68A; }
.tf--warning .tf-alerte__dot { background:#F59E0B; }
.tf--info    { background:#EFF6FF; color:#1E40AF; border-color:#BFDBFE; }
.tf--info .tf-alerte__dot { background:#3B82F6; }
@keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:none; } }

/* ═══ SKELETON ═══ */
.tf-skeleton-wrap { padding:24px 28px; }
.tf-skeleton-kpis { display:grid; grid-template-columns:repeat(6,1fr); gap:14px; }
.tf-sk { background:linear-gradient(90deg,#E8EDF3 25%,#F4F6FA 50%,#E8EDF3 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:var(--r); height:108px; }
.tf-sk--wide { border-radius:var(--r); }
@keyframes shimmer { to { background-position:-200% 0; } }

/* ═══ BODY ═══ */
.tf-body { padding:20px 28px 56px; }

/* ═══ TABS ═══ */
.tf-tabs {
    display:flex; gap:0; margin-bottom:20px;
    background:var(--surf); border:1px solid var(--border); border-radius:var(--r);
    overflow:hidden; box-shadow:var(--sh);
}
.tf-tab {
    flex:1; display:flex; align-items:center; justify-content:center; gap:7px;
    padding:12px 8px; border:none; background:transparent;
    font-size:12.5px; font-weight:500; color:var(--muted);
    cursor:pointer; transition:.15s; border-right:1px solid var(--border); position:relative;
}
.tf-tab:last-child { border-right:none; }
.tf-tab:hover { background:var(--bg); color:var(--navy); }
.tf-tab--active { background:var(--navy-lt); color:var(--navy); font-weight:700; }
.tf-tab--active::after { content:''; position:absolute; bottom:0; left:0; right:0; height:3px; background:var(--navy); }
.tf-tab__icon { display:flex; }
.tf-tab__badge { background:var(--danger); color:white; font-size:9px; font-weight:700; padding:1px 5px; border-radius:10px; }

/* ═══ SECTION ═══ */
.tf-section-title { font-size:11px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:1px; margin-bottom:14px; }
.tf-tab-content { animation:fadeIn .2s ease; }

/* ═══ KPI STRIP ═══ */
.tf-kpi-strip { display:grid; grid-template-columns:1.6fr repeat(5,1fr); gap:14px; margin-bottom:0; }
.tf-kpi { background:var(--surf); border:1px solid var(--border); border-radius:var(--r); padding:18px 20px; box-shadow:var(--sh); transition:.18s; }
.tf-kpi:hover { box-shadow:var(--sh-lg); }
.tf-kpi--hero { background:var(--navy); border-color:var(--navy); }
.tf-kpi--hero .tf-kpi__lbl, .tf-kpi--hero .tf-kpi__val, .tf-kpi--hero .tf-kpi__sub { color:rgba(255,255,255,.85); }
.tf-kpi--warn   { border-left:3px solid var(--warn); }
.tf-kpi--danger { border-left:3px solid var(--danger); }
.tf-kpi__lbl  { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; color:var(--muted); margin-bottom:8px; }
.tf-kpi__val  { font-size:26px; font-weight:700; color:var(--navy); letter-spacing:-1px; font-family:'DM Mono',monospace; line-height:1; margin-bottom:10px; }
.tf-kpi__val small { font-size:14px; font-weight:500; margin-left:2px; }
.tf-kpi__sub  { font-size:11px; color:var(--muted); }
.tf-kpi__foot { display:flex; align-items:center; gap:8px; }
.tf-kpi__prev { font-size:11.5px; color:var(--muted); }

/* ═══ BADGES delta ═══ */
.tf-badge { display:inline-flex; align-items:center; gap:2px; font-size:11px; font-weight:700; padding:2px 7px; border-radius:4px; }
.tf-badge--up      { color:#15803D; background:#F0FDF4; }
.tf-badge--down    { color:#B91C1C; background:#FEF2F2; }
.tf-badge--neutral { color:var(--muted); background:var(--bg); }
.tf-kpi--hero .tf-badge--up   { color:#4ADE80; background:rgba(74,222,128,.15); }
.tf-kpi--hero .tf-badge--down { color:#FCA5A5; background:rgba(252,165,165,.15); }

/* ═══ PROGRESS ═══ */
.tf-progress { height:4px; background:rgba(255,255,255,.2); border-radius:2px; overflow:hidden; margin-top:8px; }
.tf-kpi:not(.tf-kpi--hero) .tf-progress { background:var(--navy-lt); }
.tf-progress__fill { height:100%; background:var(--gold); border-radius:2px; transition:width .6s cubic-bezier(.4,0,.2,1); }
.tf-progress__fill--ok { background:var(--green); }

/* ═══ GRID ═══ */
.tf-grid { display:grid; gap:14px; }
.tf-grid--full { grid-template-columns:1fr; }
.tf-grid--2    { grid-template-columns:1fr 1fr; }
.tf-grid--3    { grid-template-columns:1fr 1fr 1fr; }

/* ═══ CARDS ═══ */
.tf-card { background:var(--surf); border:1px solid var(--border); border-radius:var(--r); padding:20px; box-shadow:var(--sh); transition:.18s; }
.tf-card:hover { box-shadow:var(--sh-lg); }
.tf-card--flush { padding:0; }
.tf-card--flush .tf-card__hd { padding:18px 20px 14px; }
.tf-card__hd { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:16px; }
.tf-card__hd h3 { margin:0; font-size:13.5px; font-weight:700; color:var(--navy); letter-spacing:-.2px; }
.tf-chip { font-size:10px; font-weight:700; color:var(--muted); background:var(--bg); border:1px solid var(--border); padding:3px 8px; border-radius:4px; letter-spacing:.3px; white-space:nowrap; }
.tf-chip--warn { background:#FFFBEB; color:#92400E; border-color:#FDE68A; }
.tf-chip--sm { font-size:9px; padding:2px 6px; }

/* ═══ CHART ═══ */
.tf-chart-wrap { position:relative; }
.tf-axis-lbl { font-size:10px; fill:var(--muted); font-family:'DM Mono',monospace; }

/* ═══ TABLE ═══ */
.tf-table-wrap { overflow-x:auto; }
.tf-table { width:100%; border-collapse:collapse; font-size:12.5px; }
.tf-table thead th { text-align:left; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:var(--muted); padding:0 16px 10px; border-bottom:1px solid var(--border); }
.tf-table .r { text-align:right; }
.tf-table .tf-tr td { padding:11px 16px; border-bottom:1px solid var(--border); vertical-align:middle; }
.tf-table .tf-tr:last-child td { border-bottom:none; }
.tf-table .tf-tr:hover td { background:var(--bg); }
.tf-mono { font-family:'DM Mono',monospace; font-weight:600; color:var(--navy); }

/* ═══ RANK ═══ */
.tf-rank { display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; border-radius:6px; font-size:11px; font-weight:700; background:var(--bg); color:var(--muted); }
.tf-rank.r1 { background:var(--gold-lt); color:var(--gold); border:1px solid rgba(201,168,76,.3); }
.tf-rank.r2 { background:#F1F5F9; color:#475569; }
.tf-rank.r3 { background:#FFF7ED; color:#C2410C; }

/* ═══ PERSON / AVATAR ═══ */
.tf-person { display:flex; align-items:center; gap:10px; font-weight:500; }
.tf-avatar { width:32px; height:32px; border-radius:8px; background:var(--navy-lt); color:var(--navy); font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.tf-avatar--sm { width:26px; height:26px; border-radius:6px; font-size:10px; }

/* ═══ PCT badge ═══ */
.tf-pct { font-size:12px; font-weight:700; padding:2px 8px; border-radius:4px; font-family:'DM Mono',monospace; }
.tf-pct.ok   { background:#F0FDF4; color:#15803D; }
.tf-pct.warn { background:#FFFBEB; color:#92400E; }
.tf-pct.bad  { background:#FEF2F2; color:#991B1B; }

/* ═══ INLINE BAR ═══ */
.tf-ibar { height:6px; background:var(--bg); border-radius:3px; width:100px; overflow:hidden; }
.tf-ibar__fill { height:100%; background:var(--navy); opacity:.5; border-radius:3px; transition:width .5s; }

/* ═══ OCCUPATION ═══ */
.tf-occ-list { display:flex; flex-direction:column; gap:14px; }
.tf-occ-item__head { display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; }
.tf-occ-item__sub { font-size:11px; color:var(--muted); margin-top:4px; }

/* ═══ ABSENCES ═══ */
.tf-abs-types { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.tf-abs-type  { display:flex; align-items:center; gap:6px; font-size:12px; background:var(--bg); padding:5px 10px; border-radius:6px; }
.tf-abs-type__dot { width:8px; height:8px; border-radius:50%; }
.tf-abs-type__label { flex:1; }
.tf-abs-type__nb { font-weight:700; color:var(--navy); font-family:'DM Mono',monospace; }
.tf-abs-type__j  { color:var(--muted); font-size:11px; }
.tf-abs-list { display:flex; flex-direction:column; gap:4px; }
.tf-abs-item { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:8px; font-size:12px; }
.tf-abs-item:hover { background:var(--bg); }
.tf-abs-item__who { display:flex; align-items:center; gap:8px; flex:1; }
.tf-abs-badge { font-size:10px; font-weight:700; color:white; padding:2px 7px; border-radius:4px; flex-shrink:0; }
.tf-abs-item__dates { color:var(--muted); font-size:11px; flex:1; }
.tf-abs-item__j { font-weight:700; color:var(--navy); font-family:'DM Mono',monospace; font-size:12px; }

/* ═══ FINANCE KPIs ═══ */
.tf-fin-kpis { display:grid; grid-template-columns:repeat(8,1fr); gap:12px; }
.tf-fin-kpi { background:var(--surf); border:1px solid var(--border); border-radius:var(--r); padding:14px 16px; display:flex; flex-direction:column; gap:4px; box-shadow:var(--sh); }
.tf-fin-kpi--primary { background:var(--navy); border-color:var(--navy); }
.tf-fin-kpi--primary .tf-fin-kpi__lbl, .tf-fin-kpi--primary .tf-fin-kpi__val { color:rgba(255,255,255,.85); }
.tf-fin-kpi--gold   { background:var(--gold-lt); border-color:rgba(201,168,76,.3); }
.tf-fin-kpi--gold .tf-fin-kpi__val { color:var(--gold); }
.tf-fin-kpi--warn   { border-top:3px solid var(--warn); }
.tf-fin-kpi--danger { border-top:3px solid var(--danger); }
.tf-fin-kpi__lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; color:var(--muted); }
.tf-fin-kpi__val { font-size:18px; font-weight:700; color:var(--navy); font-family:'DM Mono',monospace; letter-spacing:-.5px; }
.tf-fin-kpi__val small { font-size:12px; }

/* ═══ BARS ═══ */
.tf-bars-list { display:flex; flex-direction:column; gap:10px; }
.tf-bar-row__meta { display:flex; justify-content:space-between; margin-bottom:5px; font-size:12.5px; }
.tf-track { height:6px; background:var(--bg); border-radius:3px; overflow:hidden; }
.tf-track__fill { height:100%; background:var(--navy); border-radius:3px; transition:width .5s cubic-bezier(.4,0,.2,1); }

/* ═══ RANKED LIST ═══ */
.tf-ranked { display:flex; flex-direction:column; gap:2px; }
.tf-ranked__item { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:8px; transition:.12s; }
.tf-ranked__item:hover { background:var(--bg); }
.tf-ranked__pos  { width:22px; text-align:center; font-size:12px; font-weight:700; color:var(--muted); }
.tf-ranked__info { flex:1; }
.tf-ranked__name { display:block; font-size:13px; font-weight:500; }
.tf-ranked__sub  { font-size:11px; color:var(--muted); }
.tf-ranked__val  { font-size:13px; font-weight:700; color:var(--navy); font-family:'DM Mono',monospace; white-space:nowrap; }

/* ═══ DOT ═══ */
.tf-dot { display:inline-block; width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-right:4px; }

/* ═══ HEATMAP SEMAINE ═══ */
.tf-heatmap { display:flex; justify-content:space-between; align-items:flex-end; gap:8px; height:140px; padding-top:16px; }
.tf-hm-col  { flex:1; display:flex; flex-direction:column; align-items:center; gap:5px; }
.tf-hm-bar-wrap { flex:1; display:flex; align-items:flex-end; width:100%; justify-content:center; }
.tf-hm-bar  { width:75%; max-width:36px; border-radius:4px; background:var(--navy); transition:.4s; }
.tf-hm-lbl  { font-size:10.5px; color:var(--muted); font-weight:500; }
.tf-hm-nb   { font-size:11px; font-weight:700; color:var(--navy); font-family:'DM Mono',monospace; }

/* ═══ HOURLY ═══ */
.tf-hourly { display:flex; align-items:flex-end; gap:4px; height:100px; padding-top:8px; }
.tf-hourly__col { flex:1; display:flex; flex-direction:column; align-items:center; gap:4px; height:100%; }
.tf-hourly__bar-wrap { flex:1; display:flex; align-items:flex-end; width:100%; justify-content:center; }
.tf-hourly__bar { width:80%; background:var(--gold); border-radius:3px; transition:height .4s; min-height:3px; }
.tf-hourly__lbl { font-size:9px; color:var(--muted); }

/* ═══ RINGS ═══ */
.tf-doc-stats { display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
.tf-doc-stat  { display:flex; align-items:center; gap:12px; }
.tf-doc-stat__info { display:flex; flex-direction:column; gap:3px; }
.tf-doc-stat__label { font-size:13px; font-weight:600; color:var(--navy); }
.tf-doc-stat__sub   { font-size:11.5px; color:var(--muted); }
.tf-doc-stat__num   { font-size:36px; font-weight:700; color:var(--navy); font-family:'DM Mono',monospace; }
.tf-ring-val { font-size:14px; font-weight:700; fill:var(--navy); font-family:'DM Mono',monospace; }

/* ═══ RUPTURES ═══ */
.tf-ruptures { display:flex; align-items:center; gap:12px; background:#FEF2F2; border:1px solid #FECACA; border-radius:var(--r); padding:12px 16px; }
.tf-ruptures__icon { font-size:18px; }
.tf-ruptures__list { display:flex; flex-wrap:wrap; gap:6px; }
.tf-ruptures__item { font-size:12px; font-weight:600; color:#991B1B; background:#FEE2E2; padding:3px 9px; border-radius:4px; }

/* ═══ EMPTY ═══ */
.tf-empty { text-align:center; color:var(--muted); font-size:13px; padding:28px; }

/* ═══ RESPONSIVE ═══ */
@media (max-width:1280px) {
    .tf-kpi-strip { grid-template-columns:repeat(3,1fr); }
    .tf-fin-kpis  { grid-template-columns:repeat(4,1fr); }
    .tf-grid--3   { grid-template-columns:1fr 1fr; }
}
@media (max-width:1024px) {
    .tf-grid--2   { grid-template-columns:1fr; }
    .tf-tabs      { flex-wrap:wrap; }
}
@media (max-width:768px) {
    .tf-header { padding:0 14px; }
    .tf-body   { padding:14px 14px 48px; }
    .tf-kpi-strip { grid-template-columns:1fr 1fr; }
    .tf-fin-kpis  { grid-template-columns:1fr 1fr; }
    .tf-grid--3   { grid-template-columns:1fr; }
    .tf-header__brand { flex-shrink:0; }
    .tf-periode-nav { display:none; }
}
</style>